import Flickity from 'flickity';
import { getRTL, getBreakpoint, onLoadComplete, getIndexedHashProperty } from './_utils';
import debounce from 'lodash/debounce';
import clamp from 'clamp-js';
import { scrollToElement } from './scrollToQueryHash';

let indexedHashProperty = {};

const baseClass = 'AnimatedCardCarousel';
const activeCardClass = `${baseClass}-Card--active`;
const animateCardClass = `${baseClass}-Card--animate`;

const selectors = {
	carousel: `.${baseClass}`,
	carouselContainer: `.${baseClass}-container--desktop`,
	mobileContainer: `.${baseClass}-container--mobile`,
	slide: `.${baseClass}--slide`,
	slideStacked: `.${baseClass}--slide--stacked`,
	activeSlide: `.${baseClass}--slide.is-selected`,
	carouselList: `.${baseClass}--list`,
	card: `.${baseClass}-Card`,
	activeCard: `.${baseClass}-Card--active`,
	image: `.${baseClass}-Card-image`,
	heading: `.${baseClass}-Card-heading`,
	body: `.${baseClass}-Card-body`,
	description: `.${baseClass}-Card-description`,
	link: `.${baseClass}-Card-CTA`,
	currentSlideNumber: `.${baseClass}-pagination--currentSlideNumber`,
	desktopNav: `.${baseClass}-navigation--desktop`,
	mobileNav: `.${baseClass}-navigation--mobile`,
	nextButton: `.${baseClass}-pagination--next`,
	prevButton: `.${baseClass}-pagination--prev`,
	modals: `.WarningModal`,
};

let bp = getBreakpoint();
const isRTL = getRTL();
const direction = isRTL ? 'right' : 'left';
const landscapeImageHeight = 208.08;
const portraitImageHeight = 360;

const initCarousel = ($cardCarousel, activeIndex) => {
	const $container = $cardCarousel.find(selectors.carouselContainer);
	const $navigation = $cardCarousel.find(
		`${selectors.desktopNav} ${selectors.currentSlideNumber}`
	);
	$navigation.text(`1 ${isRTL ? '\\' : '/'} ${$navigation.data('numberOfCards')}`);
	//Init slide heights
	let tallestHeight = 0;
	$container.find(selectors.card).each(function (index) {
		const $card = $(this);
		let initialHeight = $card.find(selectors.heading).height() + portraitImageHeight;
		tallestHeight = Math.max(initialHeight, tallestHeight);
		$card.parent().attr('data-slide-index', index);
		if (index === 0) {
			$card.addClass(activeCardClass);
		} else {
			$card.css('height', portraitImageHeight + $card.find(selectors.heading).height());
		}
		$card.addClass(animateCardClass);
	});
	$container.find(`${selectors.slide}, ${selectors.card}`).css('height', tallestHeight);
	$container.find(selectors.card).css('max-height', tallestHeight + 120);

	const options = {
		cellAlign: 'center',
		pageDots: false,
		prevNextButtons: false,
		rightToLeft: isRTL,
		wrapAround: true,
		selectedAttraction: 0.075,
		friction: 0.8,
		draggable: true,
		adaptiveHeight: false,
		resize: false,
		percentPosition: false,
		imagesLoaded: false,
	};
	const $list = $container.children(selectors.carouselList);
	const $carousel = $list.get(0);

	let $initSlides = setupSlides($container);
	$container.addClass(`${baseClass}--flickity`);
	const flickity = new Flickity($carousel, options);

	$carousel.removeAttribute('tabIndex');
	$initSlides.each(function () {
		let $slideBody = $(this).find(selectors.body);
		let headingHeight = $slideBody.find(selectors.heading).height();
		let marginTop = (tallestHeight - portraitImageHeight - headingHeight) / 2;
		$slideBody.css('margin-top', marginTop);
		$slideBody.attr('data-inactive-margin', marginTop);
	});
	let $initialActiveCard = $container.find(selectors.activeCard);
	$initialActiveCard.css('height', $initialActiveCard.css('max-height'));
	$initialActiveCard.find(selectors.link).attr('tabindex', '0');
	centerBodyContent($initialActiveCard);
	fixSlideMargins($container, 0);

	flickity.on('change', function (index) {
		let $slides = $container.find(selectors.slide);
		let $prevActiveCard = $container.find(selectors.activeCard);
		let $activeCard = $container.find(`${selectors.activeSlide} ${selectors.card}`);

		$prevActiveCard.css('height', '');
		$prevActiveCard.removeClass(activeCardClass);
		$prevActiveCard.find(selectors.link).attr('tabindex', '-1');
		$prevActiveCard
			.find(selectors.body)
			.css('margin-top', $prevActiveCard.find(selectors.body).data('inactiveMargin'));
		centerBodyContent($activeCard);
		$activeCard.css('height', $activeCard.css('max-height'));
		$activeCard.addClass(activeCardClass);
		$activeCard.find(selectors.link).attr('tabindex', '0');

		$navigation.text(
			`${$($slides[flickity.selectedIndex]).data('slideIndex') + 1} ${
				isRTL ? '\\' : '/'
			} ${$navigation.data('numberOfCards')}`
		);
	});

	let prevDragX = 0;
	flickity.on('dragMove', function () {
		if (Math.abs(this.dragStartPosition - this.dragX) >= 290) {
			this.dragX = prevDragX;
		} else {
			prevDragX = this.dragX;
		}
	});

	flickity.on('staticClick', function (event, pointer, cell, index) {
		if (index !== flickity.selectedIndex) {
			if (isRTL) {
				flickity.select(
					pointer.pageX < $(window).innerWidth() / 2
						? flickity.selectedIndex + 1
						: flickity.selectedIndex - 1,
					true
				);
			} else {
				flickity.select(
					pointer.pageX > $(window).innerWidth() / 2
						? flickity.selectedIndex + 1
						: flickity.selectedIndex - 1,
					true
				);
			}
		}
	});

	$cardCarousel.find(selectors.nextButton).on('click', function () {
		flickity.select(flickity.selectedIndex + 1, true);
	});
	$cardCarousel.find(selectors.prevButton).on('click', function () {
		flickity.select(flickity.selectedIndex - 1, true);
	});

	flickity.on('scroll', function () {
		fixSlideMargins($container, flickity.selectedIndex);
	});

	$(window).on(
		'resize',
		debounce(
			function () {
				$container
					.find(selectors.slide)
					.removeClass('is-selected animated')
					.css(`margin-${direction}`, '');
			},
			{ leading: true, trailing: false },
			200
		)
	);

	$(window).on(
		'resize',
		debounce(
			function () {
				setupSlides($container, flickity);
				let $activeCard = $container.find(selectors.activeCard);
				$activeCard.parent().addClass('is-selected');
				$activeCard.find(selectors.link).attr('tabindex', '0');
				bp = getBreakpoint();
				fixSlideMargins($container, flickity.selectedIndex);
			},
			{ leading: false, trailing: true },
			200
		)
	);

	if (activeIndex > -1) {
		flickity.select(activeIndex, true);
	}

	return flickity;
};

const setupSlides = ($container, flickity) => {
	const screenWidth = $(window).innerWidth();
	let $slides = $container.find(selectors.slide);
	let minCardsNeeded = Math.max(Math.ceil(screenWidth / $slides.innerWidth()) + 2, 10);

	if (screenWidth > $slides.innerWidth() * ($slides.length - 2)) {
		//need to copy cards until we have enough
		let timesToClone = Math.floor(minCardsNeeded / $slides.length);
		//but it should be one less since we already have that amount of cards
		for (let i = 0; i < timesToClone; i++) {
			let $clones = $slides.clone();
			//Fix lazyload if it starts loading while clone is created
			$clones.find('.lazyloading').removeClass('lazyloading').addClass('lazyload');
			$clones.find(selectors.activeCard).css('height', '').removeClass(activeCardClass);
			if (flickity) {
				flickity.append($clones);
				flickity.resize();
			} else {
				$container.children(selectors.carouselList).append($clones);
			}
		}
		$slides = $container.find(selectors.slide);
	} else if (flickity) {
		flickity.resize();
	}
	$slides.each(function (index) {
		$(this).attr('data-flickity-index', index);
	});
	$slides.find(selectors.link).attr('tabindex', '-1');

	return $slides;
};

const centerBodyContent = $activeCard => {
	let $activeBody = $activeCard.find(selectors.body);
	let maxBodyHeight =
		Number.parseFloat($activeCard.css('max-height').replace('px')) - landscapeImageHeight;
	let [heading, description, ctas] = $activeBody.children();
	let headingHeight = $(heading).height(),
		ctaHeight = $(ctas).height() + 40;

	let maxHeightDesc = maxBodyHeight - headingHeight - ctaHeight - 30;
	maxHeightDesc -= maxHeightDesc % 25;
	$(description).css('max-height', maxHeightDesc);
	clamp(description, { clamp: `${maxHeightDesc}px`, useNativeClamp: true });

	let childrenHeight = headingHeight + $(description).height() + ctaHeight;
	let marginTop = (maxBodyHeight - childrenHeight - 30) / 2;
	$activeBody.css('margin-top', marginTop + childrenHeight >= maxBodyHeight ? '' : marginTop);
};

const fixSlideMargins = ($container, index) => {
	const screenWidth = $(window).innerWidth();
	const prevMargin = isRTL ? (bp === 'medium' ? 35 : 55) : bp === 'medium' ? -5 : -25,
		nextMargin = isRTL ? (bp === 'medium' ? -5 : -25) : bp === 'medium' ? 35 : 55;
	const $slides = $container.find(selectors.slide);
	// $slides.removeClass('animated');
	const $activeSlide = $container.find(`${selectors.slide}.is-selected`);
	$activeSlide.addClass('animated');
	const prevIndex = index === 0 ? $slides.length - 1 : index - 1;
	const nextIndex = index === $slides.length - 1 ? 0 : index + 1;
	const $prevSlide = $($slides.get(prevIndex));
	$prevSlide.addClass('animated').css(`margin-${direction}`, prevMargin);
	const $nextSlide = $($slides.get(nextIndex));
	$nextSlide.addClass('animated').css(`margin-${direction}`, nextMargin);
	$slides.each(function (i) {
		if (index !== i) {
			const rect = this.getBoundingClientRect();
			const rightPosition = rect['right'];
			const leftPosition = rect['left'];

			if (i !== prevIndex && i !== nextIndex) {
				if (rightPosition > screenWidth - rect.width / 2) {
					$(this).removeClass('animated');
				} else if (leftPosition < rect.width / 2) {
					$(this).removeClass('animated');
				}
			}

			if (
				i === (isRTL ? nextIndex : prevIndex) ||
				(rightPosition >= -(rect.width / 2) && rightPosition <= screenWidth / 2) ||
				leftPosition > screenWidth + rect.width / 2
			) {
				$(this).css(`margin-${direction}`, prevMargin);
			} else if (
				i === (isRTL ? prevIndex : nextIndex) ||
				(leftPosition <= screenWidth + rect.width / 2 && leftPosition >= screenWidth / 2) ||
				rightPosition < -(rect.width / 2)
			) {
				$(this).css(`margin-${direction}`, nextMargin);
			}
		} else {
			$(this).css(`margin-${direction}`, '');
		}
	});
};

const initMobileCarousel = ($cardCarousel, activeIndex) => {
	const $container = $cardCarousel.find(selectors.mobileContainer);
	const $navigation = $cardCarousel.find(
		`${selectors.mobileNav} ${selectors.currentSlideNumber}`
	);
	$navigation.text(`1 ${isRTL ? '\\' : '/'} ${$navigation.data('numberOfCards')}`);
	//Init slide heights
	let tallestHeight = 0;
	$container.find(selectors.card).each(function () {
		const $card = $(this);
		let initialHeight = $card.find(selectors.heading).height() + portraitImageHeight;
		tallestHeight = Math.max(initialHeight, tallestHeight);
		$card.css('height', portraitImageHeight + $card.find(selectors.heading).height());
		$card.addClass(animateCardClass);
	});
	$container.find(`${selectors.slide}, ${selectors.card}`).css('height', tallestHeight);

	if ($container.length > 0) {
		const options = {
			cellAlign: 'center',
			pageDots: false,
			prevNextButtons: false,
			rightToLeft: isRTL,
			wrapAround: false,
			selectedAttraction: 0.05,
			friction: 0.8,
			draggable: true,
			adaptiveHeight: false,
			resize: false,
			percentPosition: false,
			imagesLoaded: false,
			contain: true,
		};
		const $list = $container.children(selectors.carouselList);
		const $carousel = $list.get(0);
		$container.addClass(`${baseClass}--flickity`);
		const flickity = new Flickity($carousel, options);
		const $nextButton = $cardCarousel.find(selectors.nextButton);
		const $prevButton = $cardCarousel.find(selectors.prevButton);
		flickity.on('change', function () {
			$prevButton.removeClass('button-disabled');
			$nextButton.removeClass('button-disabled');
			if (flickity.selectedIndex === 0) {
				$prevButton.addClass('button-disabled');
			} else if (flickity.selectedIndex === flickity.slides.length - 1) {
				$nextButton.addClass('button-disabled');
			}
			$navigation.text(
				`${flickity.selectedIndex + 1} ${isRTL ? '\\' : '/'} ${$navigation.data(
					'numberOfCards'
				)}`
			);
		});
		$nextButton.on('click', function () {
			flickity.select(flickity.selectedIndex + 1);
		});
		$prevButton.on('click', function () {
			flickity.select(flickity.selectedIndex - 1);
		});

		$(window).on(
			'resize',
			debounce(function () {
				flickity.resize();
			}, 200)
		);

		// We will only be here if slides are not stacked
		if (activeIndex > -1) {
			flickity.select(activeIndex, true);
		}

		return flickity;
	}
};

export default function initAnimatedCardCarousels() {
	indexedHashProperty = getIndexedHashProperty();

	$(selectors.carousel).each((i, carousel) => {
		const $carousel = $(carousel);

		let activeIndex = -1;
		if (indexedHashProperty) {
			const $scrollToElements = $(`#${indexedHashProperty.id}`);
			if ($scrollToElements.length) {
				let $scrollToElement = $scrollToElements.first();
				// Check if the current carousel is a parent of the scroll to element
				if ($carousel.children(`#${indexedHashProperty.id}`).length) {
					$scrollToElement = $scrollToElement.parent();
					// Set requested page to be the active one
					activeIndex = indexedHashProperty.index - 1;
					if (activeIndex > 0) {
						// Should only find any if slides are stacked
						const stackedSlides = $carousel
							.find(selectors.slideStacked)
							.filter(':visible');
						if (stackedSlides.length >= activeIndex) {
							$scrollToElement = $(stackedSlides[activeIndex]);
						}
					}
					onLoadComplete(() => {
						scrollToElement($scrollToElement);
					});
				}
			}
		}

		if (bp === 'small') {
			initMobileCarousel($carousel, activeIndex);

			const initAfterResize = debounce(function () {
				if (getBreakpoint() !== 'small') {
					initCarousel($carousel, -1);
					$(window).off('resize', initAfterResize);
				}
			});
			$(window).on('resize', initAfterResize);
		} else {
			initCarousel($carousel, activeIndex);

			const initAfterResize = debounce(function () {
				if (getBreakpoint() === 'small') {
					initMobileCarousel($carousel, -1);
					$(window).off('resize', initAfterResize);
				}
			});
			$(window).on('resize', initAfterResize);
		}

		//move any modals out of the carousel
		$carousel.find(selectors.modals).appendTo($carousel);
	});
}
